import { createFormContext } from '@mantine/form'
import { AxiosError } from 'axios'
import { useEffect } from 'react'

import { addStoreCustomDomain, deleteStoreCustomDomain } from '@flecto/api/saas'
import { useMutationUpdateStore } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreStore } from '@flecto/types'
import { useNotifications } from '@flecto/ui'
import { useHandleAxiosError } from '@flecto/utils'

import { useCustomDomainCheck } from '../../hooks/useCustomDomainCheck'
import { useFormStoreSettings } from '../../hooks/useFormStoreSettings'
import { CardStoreAddresses } from '../CardStoreAddresses/CardStoreAddresses'
import { CardStoreContactInfo } from '../CardStoreContactInfo/CardStoreContactInfo'
import { CardStoreCustomDomain } from '../CardStoreCustomDomain/CardStoreCustomDomain'
import { CardStoreDesign } from '../CardStoreDesign/CardStoreDesign'
import { CardStoreGeneralInfo } from '../CardStoreGeneralInfo/CardStoreGeneralInfo'
import { CardStoreIframe } from '../CardStoreIframe/CardStoreIframe'
import { CardStorePrices } from '../CardStorePrices/CardStorePrices'
import { CardStoreStatus } from '../CardStoreStatus/CardStoreStatus'
import { CardStoreTerms } from '../CardStoreTerms/CardStoreTerms'
import { CardStoreUrl } from '../CardStoreUrl/CardStoreUrl'

export const [FormProvider, useFormContext, useForm] =
  createFormContext<Partial<CoreStore>>()

type FormStoreSettingsProps = {
  id?: string
  store?: Partial<CoreStore>
  onChangeLoading?: (loading: boolean) => void
  onSuccess?: () => void
}

export function FormStoreSettings({
  store,
  onChangeLoading,
  ...props
}: FormStoreSettingsProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_store' })

  const { showNotification } = useNotifications()

  const { mutateAsync: updateStore } = useMutationUpdateStore()

  const { getErrorMessages } = useHandleAxiosError()

  const form = useFormStoreSettings({ store })

  const checkDomain = useCustomDomainCheck({ form })

  useEffect(() => {
    if (store) {
      form.setValues({
        ...store,
        phone_number_country_code:
          store.phone_number_country_code !== '+' &&
          store.phone_number_country_code
            ? store.phone_number_country_code
            : '+351',
      })
    }
  }, [store])

  async function handleSubmit(values: Partial<CoreStore>) {
    const newDomain = String(values.apex_domain ?? '').toLowerCase()
    const prevDomain = String(store?.apex_domain ?? '').toLowerCase()
    const isNewDomain = newDomain !== prevDomain

    if (isNewDomain && !(await checkDomain(newDomain))) {
      return
    }

    try {
      onChangeLoading?.(true)

      if (isNewDomain) {
        if (prevDomain) {
          await deleteStoreCustomDomain(prevDomain)
        }

        if (newDomain) {
          await addStoreCustomDomain(newDomain)
        }
      }

      await updateStore({
        store: {
          ...values,
          phone_number: `${values.phone_number_country_code && values.phone_number ? values.phone_number_country_code : ''}${values.phone_number ?? ''}`,
          apex_domain: newDomain,
        },
      })

      if (isNewDomain) {
        checkDomain(newDomain)
      }

      showNotification({
        type: 'success',
        message: t('notifications.update_success'),
      })
    } catch (error) {
      showNotification({
        type: 'error',
        message: t('notifications.update_error'),
      })

      if (error instanceof AxiosError) {
        const errors = getErrorMessages(error, {
          'store[phone_number]': 'phone_number',
        })
        errors.forEach((e) => {
          form.setFieldError(e.field, e.errorMessage)
        })
      }
    } finally {
      onChangeLoading?.(false)
    }
  }

  return (
    <FormProvider form={form}>
      <form
        {...props}
        onSubmit={form.onSubmit((values) => handleSubmit(values))}
      >
        <CardStoreStatus />
        <CardStoreUrl />
        <CardStoreCustomDomain />
        <CardStoreGeneralInfo />
        <CardStoreDesign />
        <CardStorePrices />
        <CardStoreContactInfo />
        <CardStoreAddresses />
        <CardStoreTerms />
        <CardStoreIframe />
      </form>
    </FormProvider>
  )
}
